import React, { useCallback, useRef, useState } from "react";
import { Download, Eraser, FileWarning, Loader2, ShieldCheck, Trash2, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { BeforeAfter } from "./BeforeAfter";
import { analyzeFile, cleanPrepared, downloadBytes, SUPPORTED_EXT } from "../lib/processFile";
import { humanSize } from "../lib/bytes";
import { localizeReport } from "../lib/localizeReport";
import { reportCleaned } from "../lib/stats";
import { useI18n } from "../i18n";

let idSeq = 0;

const FORMAT_LABEL = {
  png: "PNG", jpeg: "JPEG", svg: "SVG", pdf: "PDF", docx: "DOCX",
  odt: "ODT", html: "HTML", markdown: "Markdown", text: "Text", video: "Video", unknown: "?",
};

const revoke = (it) => {
  if (it.result?.preview) {
    URL.revokeObjectURL(it.result.preview.before);
    URL.revokeObjectURL(it.result.preview.after);
  }
};

export default function FileScrubber() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [selected, setSelected] = useState(null);
  const [pixel, setPixel] = useState(false);
  const [strength, setStrength] = useState("maximum");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const L = useCallback((line) => localizeReport(line, lang), [lang]);

  const addFiles = useCallback(async (fileList) => {
    const files = [...fileList];
    if (!files.length) return;
    const entries = files.map((f) => ({ id: ++idSeq, name: f.name, status: "pending", prep: null, result: null }));
    setItems((prev) => [...prev, ...entries]);
    for (let i = 0; i < files.length; i++) {
      const entry = entries[i];
      await new Promise((r) => setTimeout(r, 0));
      try {
        const prep = await analyzeFile(files[i]);
        if (prep.error) {
          setItems((p) => p.map((it) => (it.id === entry.id ? { ...it, status: "unsupported" } : it)));
          continue;
        }
        setItems((p) => p.map((it) => (it.id === entry.id ? { ...it, status: "ready", prep } : it)));
        setSelected((s) => s ?? entry.id);
      } catch (e) {
        setItems((p) => p.map((it) => (it.id === entry.id ? { ...it, status: "error", error: e.message } : it)));
      }
    }
  }, []);

  const cleanAll = useCallback(async () => {
    const ready = items.filter((it) => it.status === "ready");
    if (!ready.length) return;
    setBusy(true);
    let marks = 0;
    for (const entry of ready) {
      setItems((p) => p.map((it) => (it.id === entry.id ? { ...it, status: "cleaning" } : it)));
      await new Promise((r) => setTimeout(r, 0));
      try {
        const result = await cleanPrepared(entry.prep, { pixel, pixelStrength: strength });
        marks += (result.before.findings || []).length;
        setItems((p) => p.map((it) => (it.id === entry.id ? { ...it, status: "done", result } : it)));
      } catch (e) {
        setItems((p) => p.map((it) => (it.id === entry.id ? { ...it, status: "error", error: e.message } : it)));
      }
    }
    setBusy(false);
    reportCleaned(ready.length, marks);
    toast.success(`${ready.length} × ${t("files.doneToast")}`);
  }, [items, pixel, strength, t]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const removeItem = (id) => setItems((p) => {
    const it = p.find((x) => x.id === id);
    if (it) revoke(it);
    return p.filter((x) => x.id !== id);
  });

  const clearAll = () => { items.forEach(revoke); setItems([]); setSelected(null); };

  const downloadAll = () => {
    const done = items.filter((it) => it.status === "done");
    if (!done.length) return;
    done.forEach((it, i) => setTimeout(() => downloadBytes(it.result.bytes, it.result.outName), i * 250));
    toast.success(`${done.length} × ${t("files.download")}`);
  };

  const current = items.find((it) => it.id === selected && (it.status === "ready" || it.status === "done" || it.status === "cleaning"));
  const readyCount = items.filter((it) => it.status === "ready").length;
  const detail = current?.result || current?.prep;

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
      <div className="space-y-4">
        <div
          data-testid="file-dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-16 transition-colors duration-300 ${
            dragging ? "border-sky-500/70 bg-sky-500/10" : "border-white/15 bg-white/[0.03] hover:border-sky-500/40 hover:bg-sky-500/[0.06]"
          }`}
        >
          <UploadCloud className={`h-9 w-9 transition-colors ${dragging ? "text-sky-400" : "text-slate-500"}`} strokeWidth={1.5} />
          <p className="mt-4 font-head text-lg font-medium">{t("files.drop")}</p>
          <p className="mt-1.5 text-sm text-slate-400">{t("files.or")}</p>
          <p className="mt-5 font-mono2 text-[11px] text-slate-600">{t("files.hint")}</p>
          <input
            ref={inputRef}
            data-testid="file-input"
            type="file"
            multiple
            accept={SUPPORTED_EXT.join(",")}
            className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          />
        </div>

        <div className="flex flex-col gap-3 rounded-xl border border-white/[0.07] bg-[#0c0c0e] p-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <Switch id="opt-pixel" data-testid="opt-pixel" checked={pixel} onCheckedChange={setPixel} className="data-[state=checked]:bg-sky-500" />
            <Label htmlFor="opt-pixel" className="text-[13px] font-normal text-slate-300">{t("files.optPixel")}</Label>
          </div>
          <div className={`flex flex-wrap items-center gap-1 rounded-full border border-white/10 bg-white/5 p-0.5 transition-opacity sm:ml-auto ${pixel ? "opacity-100" : "pointer-events-none opacity-40"}`} data-testid="pixel-strength">
            {["light", "balanced", "strong", "maximum", "extreme"].map((s) => (
              <button
                key={s}
                data-testid={`strength-${s}`}
                onClick={() => setStrength(s)}
                className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                  strength === s ? "bg-sky-500 text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                {t(`int.${s}`)}
              </button>
            ))}
          </div>
        </div>
        {pixel && (
          <div className="space-y-1.5">
            <p className="text-[11px] leading-relaxed text-slate-400" data-testid="pixel-note">{t("files.pixelNote")}</p>
            <p className="rounded-lg border border-amber-500/25 bg-amber-500/[0.07] px-3 py-2 text-[11px] leading-relaxed text-amber-200/90" data-testid="pixel-warn">
              {t("files.pixelWarn")}
            </p>
          </div>
        )}

        <button
          data-testid="files-clean-btn"
          disabled={!readyCount || busy}
          onClick={cleanAll}
          className="inline-flex items-center gap-2 rounded-full bg-sky-500 px-6 py-2.5 font-medium text-white shadow-[0_0_20px_rgba(14,165,233,0.3)] transition-colors hover:bg-sky-400 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-slate-500 disabled:shadow-none active:scale-[0.98]"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : <Eraser className="h-4 w-4" strokeWidth={1.75} />}
          {t("files.cleanBtn")}{readyCount ? ` (${readyCount})` : ""}
        </button>

        {current && detail && (
          <div data-testid="file-detail" className="rise rounded-xl border border-white/[0.07] bg-[#0c0c0e]">
            <div className="flex items-center gap-3 border-b border-white/[0.07] px-4 py-3">
              <span className="truncate font-head text-sm font-medium">{current.name}</span>
              <span className="rounded border border-sky-500/30 bg-sky-500/10 px-1.5 py-px font-mono2 text-[10px] text-sky-300">
                {FORMAT_LABEL[detail.format]}
              </span>
              <span className="ml-auto whitespace-nowrap font-mono2 text-[11px] text-slate-500">
                {humanSize(detail.sizeIn, lang)}
                {current.result ? ` → ${humanSize(current.result.sizeOut, lang)}` : ""}
              </span>
            </div>

            <div className="grid gap-px bg-white/[0.06] sm:grid-cols-2">
              <div className="bg-[#0c0c0e] p-4">
                <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-rose-400">{t("files.before")}</p>
                <ul data-testid="findings-before" className="space-y-1.5">
                  {detail.before.findings.length === 0 && <li className="text-[12px] text-slate-500">—</li>}
                  {detail.before.findings.map((f, i) => (
                    <li key={i} className="font-mono2 text-[11px] leading-relaxed text-slate-400">· {L(f)}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#0c0c0e] p-4">
                <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-400">{t("files.after")}</p>
                {!current.result ? (
                  <p className="text-[12px] text-slate-500">{t("files.ready")}</p>
                ) : (
                  <ul data-testid="findings-after" className="space-y-1.5">
                    {current.result.after.findings.filter((f) => !f.startsWith("info:")).length === 0 && (
                      <li className="inline-flex items-center gap-1.5 text-[12px] text-emerald-400">
                        <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.75} /> {t("files.clean_state")}
                      </li>
                    )}
                    {current.result.after.findings.map((f, i) => (
                      <li key={i} className={`font-mono2 text-[11px] leading-relaxed ${f.startsWith("info:") ? "text-slate-500" : "text-slate-400"}`}>· {L(f)}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {current.result?.preview && (
              <div className="border-t border-white/[0.07]">
                <BeforeAfter before={current.result.preview.before} after={current.result.preview.after} />
              </div>
            )}

            {current.result && (
              <div className="border-t border-white/[0.07] p-4">
                <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{t("files.actions")}</p>
                <ul data-testid="actions-list" className="space-y-1.5">
                  {current.result.actions.map((a, i) => (
                    <li key={i} className="font-mono2 text-[11px] leading-relaxed text-slate-300">→ {L(a)}</li>
                  ))}
                </ul>
                <button
                  data-testid="file-download-btn"
                  onClick={() => downloadBytes(current.result.bytes, current.result.outName)}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-sky-500 px-5 py-2 text-sm font-medium text-white shadow-[0_0_18px_rgba(14,165,233,0.3)] transition-colors hover:bg-sky-400 active:scale-[0.98]"
                >
                  <Download className="h-4 w-4" strokeWidth={1.75} /> {t("files.download")}
                </button>
              </div>
            )}
          </div>
        )}

        <p className="pt-2 text-[11px] leading-relaxed text-slate-600">{t("disclaimer")}</p>
      </div>

      <aside className="rounded-xl border border-white/[0.07] bg-[#0c0c0e]">
        <div className="flex items-center gap-2 border-b border-white/[0.07] px-4 py-2.5">
          <span className="mr-auto text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            {t("files.queue")} {items.length > 0 && <span className="text-sky-400">{items.length}</span>}
          </span>
          {items.length > 0 && (
            <>
              <button data-testid="download-all-btn" onClick={downloadAll} className="rounded-md px-2 py-1 text-xs text-slate-400 transition-colors hover:bg-white/10 hover:text-white">
                {t("files.downloadAll")}
              </button>
              <button data-testid="clear-queue-btn" aria-label={t("files.clearAll")} onClick={clearAll} className="rounded-md p-1 text-slate-400 transition-colors hover:bg-white/10 hover:text-white">
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
              </button>
            </>
          )}
        </div>

        {items.length === 0 ? (
          <p data-testid="queue-empty" className="px-4 py-8 text-center text-[13px] text-slate-600">{t("files.empty")}</p>
        ) : (
          <ul data-testid="file-queue" className="scroll-thin max-h-[32rem] divide-y divide-white/[0.05] overflow-auto">
            {items.map((it) => {
              const marks = (it.result || it.prep)?.before?.findings.length || 0;
              const saved = it.result ? Math.max(0, it.result.sizeIn - it.result.sizeOut) : 0;
              const label = marks === 1 ? t("files.marked_one") : t("files.marked");
              return (
                <li key={it.id} className={`flex items-center gap-2 pr-2 transition-colors ${selected === it.id ? "bg-sky-500/[0.08]" : "hover:bg-white/[0.04]"}`}>
                  <button
                    data-testid={`queue-item-${it.id}`}
                    onClick={() => setSelected(it.id)}
                    className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-medium text-slate-200">{it.name}</p>
                      <p className="mt-0.5 font-mono2 text-[10px] text-slate-500">
                        {it.status === "pending" && t("files.processing")}
                        {it.status === "cleaning" && t("files.cleaning")}
                        {it.status === "unsupported" && t("files.unsupported")}
                        {it.status === "error" && it.error}
                        {it.status === "ready" && `${marks} ${label} · ${t("files.ready")}`}
                        {it.status === "done" && (
                          marks === 0
                            ? t("files.clean_state")
                            : saved > 0
                              ? `${marks} ${label} · ${humanSize(saved, lang)} ${t("files.saved")}`
                              : `${marks} ${label}`
                        )}
                      </p>
                    </div>
                    {(it.status === "pending" || it.status === "cleaning") && <Loader2 className="h-4 w-4 animate-spin text-sky-400" strokeWidth={2} />}
                    {it.status === "ready" && <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />}
                    {it.status === "done" && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />}
                    {(it.status === "unsupported" || it.status === "error") && <FileWarning className="h-4 w-4 text-amber-400" strokeWidth={1.75} />}
                  </button>
                  <button
                    type="button"
                    aria-label={t("files.remove")}
                    data-testid={`queue-remove-${it.id}`}
                    onClick={() => removeItem(it.id)}
                    className="rounded p-1 text-slate-600 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={2} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </aside>
    </div>
  );
}
